import { useCallback, useMemo } from "react";
import type {
  MorphemeGlyphTileSlice,
  MorphemeGlyphTileWordMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import {
  type GlyphStyle,
  type MorphemeGlyphTileWordFront,
  WordMorphemeGlyphTileUi,
  getMorphemeGlyphTileSliceMorpheme,
  type MorphemeGlyphTileCompletion,
  type MorphemeGlyphTileStage,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import {
  type AppSelectionItem,
  morphemeSelectionItem,
} from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";

export type WordMorphemeGlyphTileLinkProps = {
  className?: string;
  completion?: MorphemeGlyphTileCompletion;
  /**
   * Curriculum word levels pass `"pinyin"` so the tile shows the readings that make up the word
   * (introduced by audio) instead of the written glyphs. Defaults to `"glyphs"`.
   */
  front?: MorphemeGlyphTileWordFront;
  /**
   * Pre-computed glyph size. Set from virtualized grids where the tile size is derivable up front
   * so the glyph renders on the first commit instead of after a per-tile `onLayout` round-trip.
   */
  glyphStyle?: GlyphStyle;
  href: string;
  /**
   * Pre-resolved word slice from a batched parent read (`useBatchedWordTileData`). When set the
   * tile fires no per-character pinyin queries.
   */
  morphemeGlyphTileSlice?: MorphemeGlyphTileWordMorphemeSlice;
  /**
   * Navigation override. When set, the tap calls this with the word instead of pushing via the
   * generic panels navigator — and the tile does NOT mount the navigator's mutations at all (a
   * `useMutation` trio per tile is real cost in a grid of hundreds). `href`/`prefixParams`/`view`
   * are ignored when set. Selecting-mode still short-circuits the tap before this runs.
   */
  onNavigate?: (word: string) => void;
  prefixParams?: MorphemeHrefObject["params"];
  /** Override the selectable leaves represented by this word (the Deck passes exact user cards). */
  selectionItems?: readonly AppSelectionItem[];
  stage?: MorphemeGlyphTileStage;
  uncolored?: boolean;
  /**
   * Which panel to open the target card on. Words only have a info view, so this only
   * matters for the grapheme variant; accepted here for a symmetric API and forwarded verbatim.
   */
  view?: "mnemonics" | "info";
  word: string;
};

type WordMorphemeGlyphTileLinkUiProps = Pick<
  WordMorphemeGlyphTileLinkProps,
  | "className"
  | "completion"
  | "front"
  | "glyphStyle"
  | "morphemeGlyphTileSlice"
  | "selectionItems"
  | "stage"
  | "uncolored"
  | "word"
> & {
  onPress: (morphemeGlyphTileSlice: MorphemeGlyphTileSlice) => void;
};

/** The selection wiring + tile shared by both navigation variants. */
function WordMorphemeGlyphTileLinkUi({
  className,
  completion,
  front,
  glyphStyle,
  morphemeGlyphTileSlice,
  onPress,
  selectionItems,
  stage,
  uncolored,
  word,
}: WordMorphemeGlyphTileLinkUiProps) {
  const defaultSelectionItems = useMemo(() => [morphemeSelectionItem(word)], [word]);
  const { consumePress, isSelected, onLongPress } = useAppSelection(
    selectionItems ?? defaultSelectionItems,
  );
  const handlePress = useCallback(
    (slice: MorphemeGlyphTileSlice) => {
      if (consumePress()) {
        return;
      }
      onPress(slice);
    },
    [consumePress, onPress],
  );

  return (
    <WordMorphemeGlyphTileUi
      className={className}
      completion={completion}
      front={front}
      glyphStyle={glyphStyle}
      isSelected={isSelected}
      morphemeGlyphTileSlice={morphemeGlyphTileSlice}
      onLongPress={onLongPress}
      onPress={handlePress}
      stage={stage}
      uncolored={uncolored}
      word={word}
    />
  );
}

function SelfNavigatingWordMorphemeGlyphTileLink({
  href,
  prefixParams,
  view,
  ...props
}: Omit<WordMorphemeGlyphTileLinkProps, "onNavigate">) {
  const cardPageNavigator = useMorphemePanelsHrefNavigator();
  const pushMutate = cardPageNavigator.push.mutate;
  const handlePress = useCallback(
    (morphemeGlyphTileSlice: MorphemeGlyphTileSlice) => {
      pushMutate({
        morpheme: getMorphemeGlyphTileSliceMorpheme(morphemeGlyphTileSlice),
        prefix: {
          href,
          params: view === undefined ? { ...prefixParams } : { ...prefixParams, view },
        },
      });
    },
    [href, prefixParams, pushMutate, view],
  );
  return <WordMorphemeGlyphTileLinkUi onPress={handlePress} {...props} />;
}

function PreparedWordMorphemeGlyphTileLink({
  onNavigate,
  ...props
}: Omit<WordMorphemeGlyphTileLinkProps, "href" | "prefixParams" | "view"> & {
  onNavigate: (word: string) => void;
}) {
  const { word } = props;
  const handlePress = useCallback(() => {
    onNavigate(word);
  }, [onNavigate, word]);
  return <WordMorphemeGlyphTileLinkUi onPress={handlePress} {...props} />;
}

export function WordMorphemeGlyphTileLink({
  href,
  onNavigate,
  prefixParams,
  view,
  ...props
}: WordMorphemeGlyphTileLinkProps) {
  if (onNavigate !== undefined) {
    return <PreparedWordMorphemeGlyphTileLink onNavigate={onNavigate} {...props} />;
  }
  return (
    <SelfNavigatingWordMorphemeGlyphTileLink
      href={href}
      prefixParams={prefixParams}
      view={view}
      {...props}
    />
  );
}
